import type { ReactNode } from 'react'

import styles from './DeviceFrame.module.css'

/**
 * The phone the app is drawn on when the screen is wider than one.
 *
 * anytracker has a single layout, and it is the phone's. A wide screen does not
 * get a rearranged version of the app. It gets the same one, inside a
 * phone-shaped frame, so there is never a desktop layout that has drifted from
 * the phone layout or is missing one of its controls.
 *
 * On a phone all three elements are `display: contents` and add nothing. On a
 * wide screen:
 *
 * - `.device` is transformed, which makes it the containing block for every
 *   `position: fixed` descendant. The bottom navigation, toasts, the log flow
 *   and prompts pin to the phone rather than to the browser window, with no
 *   changes to the components themselves.
 * - `.screen` is the scroll container, and sits *inside* `.device`, so fixed
 *   elements stay put while the content scrolls beneath them, as they would on
 *   a phone.
 *
 * Sheets are the exception: a modal `<dialog>` is drawn in the top layer, which
 * no ancestor can contain. `Sheet.module.css` places them over the frame using
 * the same tokens this file uses for its size.
 */
export function DeviceFrame({ children }: { readonly children: ReactNode }) {
  return (
    <div className={styles['stage']}>
      <div className={styles['device']}>
        <div className={styles['screen']}>{children}</div>
      </div>
    </div>
  )
}
